import SVG from '@app/components/SVG';
import styled from '@emotion/styled';
import EFTWarningImage from '@supplierRegistration/images/warning-white-orange.svg';
import { observer } from 'mobx-react';
import React, { FC, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  BoldText,
  HighlightedInstructionsAdditional,
  HighlightedInstructionsContainer,
  InstructionsCard,
  InstructionsSubTitleCard,
  InstructionsTitleCard,
  NormalText,
  PaymentInstructionsLineHr,
} from '../Styles';
import { WireTransferWarningBox } from '../supplierRegistrationProcess/AfterFormPage';
import CurrencyChips from './CurrencyChips';
import { InstructionFormDetails } from './InstructionFormDetails';

interface Props {
  instructionDetails: InstructionFormDetails;
}

const TransferInstructionForm: FC<Props> = observer(({ instructionDetails }) => {
  const { t } = useTranslation();

  const getInstructionLines = (line: { description: string; value: string }[]): ReactNode => {
    return line.map((line) => (
      <React.Fragment key={line.description}>
        <PaymentInstructionsLine withMarginTop={false}>
          <BoldText>{t(line.description)}</BoldText>
          <NormalText>{t(line.value)}</NormalText>
        </PaymentInstructionsLine>
        <PaymentInstructionsLineHr />
      </React.Fragment>
    ));
  };

  const getExtraInstructions = (extraInstructions): ReactNode => {
    if (extraInstructions.length === 1) {
      return (
        <ExtraInstructionsContainer>
          <StepCard>
            <StepIcon>
              {extraInstructions[0].icon && (
                <SVG height={76} accessibilityLabel={t('general.accessibility.step')} image={extraInstructions[0].icon} />
              )}
            </StepIcon>
            <StepContent>
              <StepText>{t(extraInstructions[0].text)}</StepText>
            </StepContent>
          </StepCard>
        </ExtraInstructionsContainer>
      );
    } else if (extraInstructions.length === 2) {
      return (
        <ExtraInstructionsContainer>
          <StepCard>
            <StepIcon>
              {extraInstructions[0].icon && (
                <SVG height={76} accessibilityLabel={t('general.accessibility.step')} image={extraInstructions[0].icon} />
              )}
            </StepIcon>
            <StepContent>
              <StepTitle>Step 1</StepTitle>
              <StepText>{t(extraInstructions[0].text)}</StepText>
            </StepContent>
          </StepCard>
          <StepCard>
            <StepIcon>
              {extraInstructions[1].icon && (
                <SVG height={76} accessibilityLabel={t('general.accessibility.step')} image={extraInstructions[1].icon} />
              )}
            </StepIcon>
            <StepContent>
              <StepTitle>Step 2</StepTitle>
              <StepText>{t(extraInstructions[1].text)}</StepText>
            </StepContent>
          </StepCard>
        </ExtraInstructionsContainer>
      );
    } else {
      return null;
    }
  };

  const renderHighlightedInstructions = (highlightedInstructions: {
    title?: string;
    additionalItems?: string[];
  }): React.ReactNode => {
    return (
      <HighlightedInstructionsContainer>
        {highlightedInstructions.additionalItems?.map((item, index) => (
          <HighlightedInstructionsAdditional key={`highlighted-inst-${index}`}>{t(item)}</HighlightedInstructionsAdditional>
        ))}
      </HighlightedInstructionsContainer>
    );
  };

  return (
    <div>
      <InstructionsSubTitle>
        {t(instructionDetails.instructionsSubTitle)}
        {(instructionDetails?.extraInstructions?.length ?? 0) > 0 && getExtraInstructions(instructionDetails.extraInstructions)}
        {instructionDetails.highlightedInstructions && renderHighlightedInstructions(instructionDetails.highlightedInstructions)}
      </InstructionsSubTitle>
      {instructionDetails.specialComponent ? (
        instructionDetails.specialComponent
      ) : (
        <PaymentInstructionsContainer>
          {instructionDetails?.currencies.length > 0 && <CurrencyChips supportedCurrencies={instructionDetails.currencies} />}
          <InstructionsTitleCard>
            {t(instructionDetails.paymentInstructionsTitle)}
            {instructionDetails.showWireTransferWarningBox.achWireTransfer && (
              <InstructionsSubTitleCard>
                {t('supplierValidation.afterRegister.onlySwift')} / {t('supplierValidation.afterRegister.doNotUseACH')}
              </InstructionsSubTitleCard>
            )}
          </InstructionsTitleCard>

          <InstructionsCard customBackgroundColor='#ffffff' noPadding>
            {instructionDetails.showWireTransferWarningBox.swiftWireTransfer && (
              <WireTransferWarningBox>
                <EFTWarningBox>
                  <EFTWarningSVG accessibilityLabel={t('general.accessibility.warning')} image={EFTWarningImage} height={30} />
                  <EFTWarningText>
                    <strong>{t('supplierValidation.afterRegister.onlyEft')}</strong>
                    <EFTWarningSubtext>
                      <Trans i18nKey='supplierValidation.afterRegister.doNotSendWires'>
                        <strong>Do not send Wires,</strong> interac/e-transfer payments- they will be rejected.
                      </Trans>
                    </EFTWarningSubtext>
                  </EFTWarningText>
                </EFTWarningBox>
              </WireTransferWarningBox>
            )}
            <AccountDetailsDiv>
              {getInstructionLines(instructionDetails.paymentInstructionsLines)}
              <PaymentInstructionsLine>
                <BoldText>{t(instructionDetails.paymentInstructionsAddress.description)}</BoldText>
                {instructionDetails.paymentInstructionsAddress.value}
              </PaymentInstructionsLine>
            </AccountDetailsDiv>
          </InstructionsCard>
        </PaymentInstructionsContainer>
      )}
    </div>
  );
});

export default TransferInstructionForm;

const PaymentInstructionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  background-color: #f5f6fa;
`;

const InstructionsSubTitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  letter-spacing: normal;
  color: #191919;
  margin-bottom: 20px;
  align-items: center;
  text-align: center;
`;

const ExtraInstructionsContainer = styled.div`
  display: flex;
  border: 1px solid var(--black-weak);
  border-radius: 2px;
  flex-direction: row;
  gap: 30px;
  justify-content: center;
  align-items: center;
  padding: 24px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const EFTWarningBox = styled.div`
  background-color: #ff9500;
  border-radius: 50px;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  padding: 5px 20px;
  margin-top: 10px;
  margin-bottom: 10px;
  gap: 10px;
`;

const EFTWarningText = styled.div`
  color: #eefcff;
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

const EFTWarningSubtext = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;

const EFTWarningSVG = styled(SVG)``;

const AccountDetailsDiv = styled.div`
  padding-top: 14px;
  padding-bottom: 14px;
  background-color: #ffffff;
`;

const PaymentInstructionsLine = styled.div<{ withMarginTop?: boolean }>`
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  flex-direction: row;
  align-items: baseline;

  &:first-child {
    margin-top: ${(p): number => (p.withMarginTop ? 8 : 0)}px;
  }

  & > div {
    flex: 1;
  }
`;

const StepCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const StepIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const StepTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
`;

const StepText = styled.div`
  text-align: left;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
`;
